import { ChangeRequestRow, ChangePlanRow, SecurityReviewRow } from '@/types'
import { Ship } from '@/types'

/* generate html export for change request record */
export function generateChangeRecordHTML(
  changeRequest: ChangeRequestRow,
  ship: Ship | null,
  changePlan: ChangePlanRow | null,
  securityReview: SecurityReviewRow | null,
  systemName?: string | null
): string {
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  const formatStatus = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Change Request Record - ${changeRequest.change_request_no || 'N/A'}</title>
  <style>
    @media print {
      @page {
        size: A4;
        margin: 1.5cm;
      }
      body {
        margin: 0;
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #333;
      background: #fff;
      padding: 20px;
    }
    .header {
      border-bottom: 3px solid #1e40af;
      padding-bottom: 15px;
      margin-bottom: 20px;
    }
    .header h1 {
      color: #1e40af;
      font-size: 24pt;
      margin-bottom: 10px;
    }
    .header-info {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      font-size: 10pt;
    }
    .header-info-item {
      display: flex;
      gap: 5px;
    }
    .header-info-label {
      font-weight: 600;
      color: #555;
    }
    .section {
      margin-bottom: 25px;
      page-break-inside: avoid;
    }
    .section-title {
      background: #1e40af;
      color: white;
      padding: 8px 12px;
      font-size: 14pt;
      font-weight: 600;
      margin-bottom: 12px;
      border-radius: 4px;
    }
    .subsection-title {
      background: #e0e7ff;
      color: #1e40af;
      padding: 6px 10px;
      font-size: 12pt;
      font-weight: 600;
      margin: 15px 0 10px 0;
      border-left: 4px solid #1e40af;
    }
    .field-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      margin-bottom: 15px;
    }
    .field {
      margin-bottom: 10px;
    }
    .field-label {
      font-weight: 600;
      color: #555;
      font-size: 10pt;
      margin-bottom: 3px;
    }
    .field-value {
      color: #333;
      padding: 5px 8px;
      background: #f8f9fa;
      border-radius: 3px;
      min-height: 24px;
    }
    .field-value.empty {
      color: #999;
      font-style: italic;
    }
    .field-full {
      grid-column: 1 / -1;
    }
    .field-full .field-value {
      min-height: 60px;
      white-space: pre-wrap;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    table th, table td {
      padding: 8px;
      text-align: left;
      border: 1px solid #ddd;
    }
    table th {
      background: #f0f0f0;
      font-weight: 600;
      font-size: 10pt;
    }
    table td {
      font-size: 10pt;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 9pt;
      font-weight: 600;
    }
    .status-draft { background: #e5e7eb; color: #374151; }
    .status-under_review { background: #fef3c7; color: #92400e; }
    .status-approved { background: #d1fae5; color: #065f46; }
    .status-implemented { background: #dbeafe; color: #1e40af; }
    .status-closed { background: #f3f4f6; color: #4b5563; }
    .footer {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 2px solid #ddd;
      text-align: center;
      font-size: 9pt;
      color: #666;
    }
    .print-actions {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 1000;
      display: flex;
      gap: 10px;
    }
    .btn {
      padding: 10px 20px;
      background: #1e40af;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12pt;
      font-weight: 600;
    }
    .btn:hover {
      background: #1e3a8a;
    }
    .btn-print {
      background: #059669;
    }
    .btn-print:hover {
      background: #047857;
    }
    .risk-classification {
      background: #fef3c7;
      border: 2px solid #f59e0b;
      border-radius: 4px;
      padding: 12px;
      margin: 20px 0;
    }
    .risk-classification-title {
      font-weight: 700;
      font-size: 12pt;
      color: #92400e;
      margin-bottom: 8px;
    }
    .risk-classification-item {
      display: flex;
      gap: 10px;
      margin: 5px 0;
    }
    .risk-classification-label {
      font-weight: 600;
      color: #78350f;
      min-width: 120px;
    }
    .sign-off-section {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #1e40af;
      page-break-inside: avoid;
    }
    .sign-off-title {
      font-weight: 700;
      font-size: 14pt;
      color: #1e40af;
      margin-bottom: 20px;
    }
    .sign-off-line {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin: 25px 0;
      padding-bottom: 5px;
      border-bottom: 1px solid #333;
    }
    .sign-off-label {
      font-weight: 600;
      font-size: 11pt;
    }
    .sign-off-blank {
      flex: 1;
      border-bottom: 1px solid #333;
      margin: 0 20px;
      min-width: 200px;
    }
  </style>
</head>
<body>
  <div class="no-print print-actions">
    <button class="btn btn-print" onclick="window.print()">Print / Save as PDF</button>
    <button class="btn" onclick="window.close()">Close</button>
  </div>

  <div class="header">
    <h1>Management of Change (MoC) Record</h1>
    <div class="header-info">
      <div class="header-info-item">
        <span class="header-info-label">Vessel:</span>
        <span>${ship?.vessel_name || 'N/A'}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">IMO Number:</span>
        <span>${ship?.imo_number || 'N/A'}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">Yard Number:</span>
        <span>${ship?.hull_number || 'N/A'}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">Class ID:</span>
        <span>${ship?.class_society_id || 'N/A'}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">Change Request No:</span>
        <span>${changeRequest.change_request_no || 'N/A'}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">Ship ID:</span>
        <span>${changeRequest.ship_id || 'N/A'}</span>
      </div>
      ${changeRequest.system_190_id ? `
      <div class="header-info-item">
        <span class="header-info-label">System:</span>
        <span>${systemName ? `${systemName} (ID: ${changeRequest.system_190_id})` : `ID: ${changeRequest.system_190_id}`}</span>
      </div>
      ` : ''}
      <div class="header-info-item">
        <span class="header-info-label">Created:</span>
        <span>${formatDate(changeRequest.created_at)}</span>
      </div>
      <div class="header-info-item">
        <span class="header-info-label">Last Updated:</span>
        <span>${formatDate(changeRequest.updated_at)}</span>
      </div>
    </div>
  </div>

  <!-- Risk Classification Section -->
  ${changeRequest.category || changeRequest.risk_level ? `
  <div class="risk-classification">
    <div class="risk-classification-title">Risk Classification</div>
    ${changeRequest.category ? `
    <div class="risk-classification-item">
      <span class="risk-classification-label">Category:</span>
      <span>${changeRequest.category}</span>
    </div>
    ` : ''}
    ${changeRequest.risk_level ? `
    <div class="risk-classification-item">
      <span class="risk-classification-label">Risk Level:</span>
      <span>${changeRequest.risk_level}</span>
    </div>
    ` : ''}
  </div>
  ` : ''}

  <!-- Status Section -->
  <div class="section">
    <div class="section-title">Status & Approval Information</div>
    <div class="field-grid">
      <div class="field">
        <div class="field-label">Status</div>
        <div class="field-value">
          <span class="status-badge status-${changeRequest.status}">${formatStatus(changeRequest.status || 'N/A')}</span>
        </div>
      </div>
      <div class="field">
        <div class="field-label">Approval Status</div>
        <div class="field-value">
          <span class="status-badge status-${changeRequest.approval_status}">${formatStatus(changeRequest.approval_status || 'N/A')}</span>
        </div>
      </div>
      ${changeRequest.category ? `
      <div class="field">
        <div class="field-label">Category</div>
        <div class="field-value">${changeRequest.category}</div>
      </div>
      ` : ''}
      ${changeRequest.approved_at ? `
      <div class="field">
        <div class="field-label">Approved At</div>
        <div class="field-value">${formatDate(changeRequest.approved_at)}</div>
      </div>
      ` : ''}
      ${changeRequest.implemented_at ? `
      <div class="field">
        <div class="field-label">Implemented At</div>
        <div class="field-value">${formatDate(changeRequest.implemented_at)}</div>
      </div>
      ` : ''}
      ${changeRequest.rejection_reason ? `
      <div class="field field-full">
        <div class="field-label">Rejection Reason</div>
        <div class="field-value">${changeRequest.rejection_reason}</div>
      </div>
      ` : ''}
      ${changeRequest.test_summary ? `
      <div class="field field-full">
        <div class="field-label">Final Onboard Test Summary</div>
        <div class="field-value">${changeRequest.test_summary}</div>
      </div>
      ` : ''}
    </div>
  </div>

  <!-- Vendor Section -->
  <div class="section">
    <div class="section-title">Vendor Information</div>
    <div class="field-grid">
      <div class="field">
        <div class="field-label">Type</div>
        <div class="field-value">${changeRequest.type ? changeRequest.type.charAt(0).toUpperCase() + changeRequest.type.slice(1) : 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Request Date</div>
        <div class="field-value">${formatDate(changeRequest.request_date)}</div>
      </div>
      <div class="field">
        <div class="field-label">Requester Department</div>
        <div class="field-value ${!changeRequest.requester_department ? 'empty' : ''}">${changeRequest.requester_department || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Requester Position</div>
        <div class="field-value ${!changeRequest.requester_position ? 'empty' : ''}">${changeRequest.requester_position || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Requester Name</div>
        <div class="field-value ${!changeRequest.requester_name ? 'empty' : ''}">${changeRequest.requester_name || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Installed CBS</div>
        <div class="field-value ${!changeRequest.installed_cbs ? 'empty' : ''}">${changeRequest.installed_cbs || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Installed Component</div>
        <div class="field-value ${!changeRequest.installed_component ? 'empty' : ''}">${changeRequest.installed_component || 'N/A'}</div>
      </div>
      <div class="field field-full">
        <div class="field-label">Reason for Change</div>
        <div class="field-value ${!changeRequest.reason ? 'empty' : ''}">${changeRequest.reason || 'N/A'}</div>
      </div>
    </div>

    <div class="subsection-title">Before Change</div>
    <div class="field-grid">
      <div class="field">
        <div class="field-label">Vendor/Model</div>
        <div class="field-value ${!changeRequest.before_vendor_model ? 'empty' : ''}">${changeRequest.before_vendor_model || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Item Name</div>
        <div class="field-value ${!changeRequest.before_item_name ? 'empty' : ''}">${changeRequest.before_item_name || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">OS Name/Version</div>
        <div class="field-value ${!changeRequest.before_os_name_version ? 'empty' : ''}">${changeRequest.before_os_name_version || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Software Version</div>
        <div class="field-value ${!changeRequest.before_sw_version ? 'empty' : ''}">${changeRequest.before_sw_version || 'N/A'}</div>
      </div>
    </div>

    <div class="subsection-title">After Change</div>
    <div class="field-grid">
      <div class="field">
        <div class="field-label">Vendor/Model</div>
        <div class="field-value ${!changeRequest.after_vendor_model ? 'empty' : ''}">${changeRequest.after_vendor_model || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Item Name</div>
        <div class="field-value ${!changeRequest.after_item_name ? 'empty' : ''}">${changeRequest.after_item_name || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">OS Name/Version</div>
        <div class="field-value ${!changeRequest.after_os_name_version ? 'empty' : ''}">${changeRequest.after_os_name_version || 'N/A'}</div>
      </div>
      <div class="field">
        <div class="field-label">Software Version</div>
        <div class="field-value ${!changeRequest.after_sw_version ? 'empty' : ''}">${changeRequest.after_sw_version || 'N/A'}</div>
      </div>
    </div>

    <div class="field-grid">
      <div class="field field-full">
        <div class="field-label">Work Description</div>
        <div class="field-value ${!changeRequest.work_description ? 'empty' : ''}">${changeRequest.work_description || 'N/A'}</div>
      </div>
      <div class="field field-full">
        <div class="field-label">Security Notes</div>
        <div class="field-value ${!changeRequest.requestor_security_note ? 'empty' : ''}">${changeRequest.requestor_security_note || 'N/A'}</div>
      </div>
    </div>
  </div>

  ${(() => {
    // For Cat I, only show E22 sections if they have data
    // For Cat II/III, show all E22 sections
    const isCatI = changeRequest.category === 'Cat I'
    const isCatIIOrIII = changeRequest.category === 'Cat II' || changeRequest.category === 'Cat III'
    
    // Check if Impact Analysis section has data
    const hasImpactData = changeRequest.criticality || changeRequest.system_category || 
      changeRequest.risk_level !== null || changeRequest.requires_class_approval !== null || 
      changeRequest.impact_summary || changeRequest.analysis_at
    
    // Check if Backup & Rollback section has data
    const hasBackupData = changeRequest.backup_completed !== null || changeRequest.rollback_possible !== null ||
      changeRequest.backup_details || changeRequest.rollback_plan || 
      changeRequest.rollback_executed || changeRequest.rollback_reason
    
    // Check if Testing section has data
    const hasTestingData = changeRequest.pre_test_required || changeRequest.pre_test_summary || 
      changeRequest.pre_test_evidence || changeRequest.post_test_required || 
      changeRequest.post_test_summary || changeRequest.post_test_evidence
    
    // Check if Traceability section has data
    const hasTraceabilityData = changeRequest.software_artifact_ref || 
      changeRequest.checksum_or_signature || changeRequest.config_version
    
    // Check if Stakeholders section has data
    const hasStakeholdersData = changeRequest.vendor_notified || changeRequest.vendor_name ||
      changeRequest.shipyard_notified || changeRequest.class_approval_ref || changeRequest.other_stakeholders
    
    // Check if Post-Review section has data
    const hasPostReviewData = changeRequest.post_implementation_review || changeRequest.cab_review
    
    // Show sections based on category
    const showImpact = isCatIIOrIII || (isCatI && hasImpactData)
    const showBackup = isCatIIOrIII || (isCatI && hasBackupData)
    const showTesting = isCatIIOrIII || (isCatI && hasTestingData)
    const showTraceability = isCatIIOrIII || (isCatI && hasTraceabilityData)
    const showStakeholders = isCatIIOrIII || (isCatI && hasStakeholdersData)
    const showPostReview = isCatIIOrIII || (isCatI && hasPostReviewData)
    
    let html = ''
    
    if (showImpact) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Impact Analysis</div>
    <div class="field-grid">
      ${changeRequest.criticality ? `
      <div class="field">
        <div class="field-label">Criticality</div>
        <div class="field-value">${changeRequest.criticality}</div>
      </div>
      ` : ''}
      ${changeRequest.system_category ? `
      <div class="field">
        <div class="field-label">System Category</div>
        <div class="field-value">${changeRequest.system_category}</div>
      </div>
      ` : ''}
      ${changeRequest.risk_level ? `
      <div class="field">
        <div class="field-label">Risk Level</div>
        <div class="field-value">${changeRequest.risk_level}</div>
      </div>
      ` : ''}
      ${changeRequest.requires_class_approval !== null ? `
      <div class="field">
        <div class="field-label">Requires Class Approval</div>
        <div class="field-value">${changeRequest.requires_class_approval ? 'Yes' : 'No'}</div>
      </div>
      ` : ''}
      ${changeRequest.impact_summary ? `
      <div class="field field-full">
        <div class="field-label">Impact Summary</div>
        <div class="field-value">${changeRequest.impact_summary}</div>
      </div>
      ` : ''}
      ${changeRequest.analysis_at ? `
      <div class="field">
        <div class="field-label">Analysis Date</div>
        <div class="field-value">${formatDate(changeRequest.analysis_at)}</div>
      </div>
      ` : ''}
    </div>
  </div>
      `
    }
    
    if (showBackup) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Backup & Rollback</div>
    <div class="field-grid">
      ${changeRequest.backup_completed !== null ? `
      <div class="field">
        <div class="field-label">Backup Completed</div>
        <div class="field-value">${changeRequest.backup_completed ? 'Yes' : 'No'}</div>
      </div>
      ` : ''}
      ${changeRequest.rollback_possible !== null ? `
      <div class="field">
        <div class="field-label">Rollback Possible</div>
        <div class="field-value">${changeRequest.rollback_possible ? 'Yes' : 'No'}</div>
      </div>
      ` : ''}
      ${changeRequest.backup_details ? `
      <div class="field field-full">
        <div class="field-label">Backup Details</div>
        <div class="field-value">${changeRequest.backup_details}</div>
      </div>
      ` : ''}
      ${changeRequest.rollback_plan ? `
      <div class="field field-full">
        <div class="field-label">Rollback Plan</div>
        <div class="field-value">${changeRequest.rollback_plan}</div>
      </div>
      ` : ''}
      ${changeRequest.rollback_executed ? `
      <div class="field field-full">
        <div class="field-label">Rollback Executed</div>
        <div class="field-value">Yes - ${formatDate(changeRequest.rollback_at)}</div>
      </div>
      ` : ''}
      ${changeRequest.rollback_reason ? `
      <div class="field field-full">
        <div class="field-label">Rollback Reason</div>
        <div class="field-value">${changeRequest.rollback_reason}</div>
      </div>
      ` : ''}
    </div>
  </div>
      `
    }
    
    if (showTesting) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Testing</div>
    <div class="field-grid">
      ${changeRequest.pre_test_required ? `
      <div class="subsection-title">Pre-Implementation Testing</div>
      ${changeRequest.pre_test_summary ? `
      <div class="field field-full">
        <div class="field-label">Pre-Test Summary</div>
        <div class="field-value">${changeRequest.pre_test_summary}</div>
      </div>
      ` : ''}
      ${changeRequest.pre_test_evidence ? `
      <div class="field field-full">
        <div class="field-label">Pre-Test Evidence</div>
        <div class="field-value">${changeRequest.pre_test_evidence}</div>
      </div>
      ` : ''}
      ` : ''}
      ${changeRequest.post_test_required ? `
      <div class="subsection-title">Post-Implementation Testing</div>
      ${changeRequest.post_test_summary ? `
      <div class="field field-full">
        <div class="field-label">Post-Test Summary</div>
        <div class="field-value">${changeRequest.post_test_summary}</div>
      </div>
      ` : ''}
      ${changeRequest.post_test_evidence ? `
      <div class="field field-full">
        <div class="field-label">Post-Test Evidence</div>
        <div class="field-value">${changeRequest.post_test_evidence}</div>
      </div>
      ` : ''}
      ` : ''}
    </div>
  </div>
      `
    }
    
    if (showTraceability) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Traceability</div>
    <div class="field-grid">
      ${changeRequest.software_artifact_ref ? `
      <div class="field">
        <div class="field-label">Software Artifact Reference</div>
        <div class="field-value">${changeRequest.software_artifact_ref}</div>
      </div>
      ` : ''}
      ${changeRequest.checksum_or_signature ? `
      <div class="field">
        <div class="field-label">Checksum or Signature</div>
        <div class="field-value">${changeRequest.checksum_or_signature}</div>
      </div>
      ` : ''}
      ${changeRequest.config_version ? `
      <div class="field">
        <div class="field-label">Configuration Version</div>
        <div class="field-value">${changeRequest.config_version}</div>
      </div>
      ` : ''}
    </div>
  </div>
      `
    }
    
    if (showStakeholders) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Stakeholders</div>
    <div class="field-grid">
      ${changeRequest.vendor_notified ? `
      <div class="field">
        <div class="field-label">Vendor Notified</div>
        <div class="field-value">Yes${changeRequest.vendor_name ? ` - ${changeRequest.vendor_name}` : ''}</div>
      </div>
      ` : ''}
      ${changeRequest.shipyard_notified ? `
      <div class="field">
        <div class="field-label">Shipyard Notified</div>
        <div class="field-value">Yes</div>
      </div>
      ` : ''}
      ${changeRequest.class_approval_ref ? `
      <div class="field">
        <div class="field-label">Class Approval Reference</div>
        <div class="field-value">${changeRequest.class_approval_ref}</div>
      </div>
      ` : ''}
      ${changeRequest.other_stakeholders ? `
      <div class="field field-full">
        <div class="field-label">Other Stakeholders</div>
        <div class="field-value">${changeRequest.other_stakeholders}</div>
      </div>
      ` : ''}
    </div>
  </div>
      `
    }
    
    if (showPostReview) {
      html += `
  <div class="section">
    <div class="section-title">E22 §6 Compliance - Post-Review</div>
    <div class="field-grid">
      ${changeRequest.post_implementation_review ? `
      <div class="field field-full">
        <div class="field-label">Post-Implementation Review</div>
        <div class="field-value">${changeRequest.post_implementation_review}</div>
      </div>
      ` : ''}
      ${changeRequest.cab_review ? `
      <div class="field field-full">
        <div class="field-label">Change Advisory Board (CAB) Review</div>
        <div class="field-value">${changeRequest.cab_review}</div>
      </div>
      ` : ''}
    </div>
  </div>
      `
    }
    
    return html
  })()}

  ${changePlan ? `
  <div class="section">
    <div class="section-title">Change Plan</div>
    <div class="field-grid">
      ${changePlan.plan_date ? `
      <div class="field">
        <div class="field-label">Plan Date</div>
        <div class="field-value">${formatDate(changePlan.plan_date)}</div>
      </div>
      ` : ''}
      ${changePlan.author_department ? `
      <div class="field">
        <div class="field-label">Author Department</div>
        <div class="field-value">${changePlan.author_department}</div>
      </div>
      ` : ''}
      ${changePlan.author_position ? `
      <div class="field">
        <div class="field-label">Author Position</div>
        <div class="field-value">${changePlan.author_position}</div>
      </div>
      ` : ''}
      ${changePlan.author_name ? `
      <div class="field">
        <div class="field-label">Author Name</div>
        <div class="field-value">${changePlan.author_name}</div>
      </div>
      ` : ''}
      ${changePlan.plan_details ? `
      <div class="field field-full">
        <div class="field-label">Plan Details</div>
        <div class="field-value">${changePlan.plan_details}</div>
      </div>
      ` : ''}
      ${changePlan.plan_security_comment ? `
      <div class="field field-full">
        <div class="field-label">Plan Security Comment</div>
        <div class="field-value">${changePlan.plan_security_comment}</div>
      </div>
      ` : ''}
    </div>
  </div>
  ` : ''}

  ${securityReview ? `
  <div class="section">
    <div class="section-title">Security Review</div>
    <div class="field-grid">
      ${securityReview.report_date ? `
      <div class="field">
        <div class="field-label">Report Date</div>
        <div class="field-value">${formatDate(securityReview.report_date)}</div>
      </div>
      ` : ''}
      ${securityReview.review_date ? `
      <div class="field">
        <div class="field-label">Review Date</div>
        <div class="field-value">${formatDate(securityReview.review_date)}</div>
      </div>
      ` : ''}
      ${securityReview.reviewer_department ? `
      <div class="field">
        <div class="field-label">Reviewer Department</div>
        <div class="field-value">${securityReview.reviewer_department}</div>
      </div>
      ` : ''}
      ${securityReview.reviewer_position ? `
      <div class="field">
        <div class="field-label">Reviewer Position</div>
        <div class="field-value">${securityReview.reviewer_position}</div>
      </div>
      ` : ''}
      ${securityReview.reviewer_name ? `
      <div class="field">
        <div class="field-label">Reviewer Name</div>
        <div class="field-value">${securityReview.reviewer_name}</div>
      </div>
      ` : ''}
      ${securityReview.overall_comment ? `
      <div class="field field-full">
        <div class="field-label">Overall Comment</div>
        <div class="field-value">${securityReview.overall_comment}</div>
      </div>
      ` : ''}
      ${securityReview.items ? `
      <div class="field field-full">
        <div class="field-label">Review Items</div>
        <div class="field-value" style="font-family: monospace; font-size: 9pt;">${JSON.stringify(securityReview.items, null, 2)}</div>
      </div>
      ` : ''}
    </div>
  </div>
  ` : ''}

  <!-- Sign-off Section -->
  <div class="sign-off-section">
    <div class="sign-off-title">Sign-off</div>
    <div class="sign-off-line">
      <span class="sign-off-label">Prepared by:</span>
      <span class="sign-off-blank"></span>
      <span class="sign-off-label">Date:</span>
      <span class="sign-off-blank"></span>
    </div>
    <div class="sign-off-line">
      <span class="sign-off-label">Reviewed by:</span>
      <span class="sign-off-blank"></span>
      <span class="sign-off-label">Date:</span>
      <span class="sign-off-blank"></span>
    </div>
    <div class="sign-off-line">
      <span class="sign-off-label">Approved by:</span>
      <span class="sign-off-blank"></span>
      <span class="sign-off-label">Date:</span>
      <span class="sign-off-blank"></span>
    </div>
  </div>

  <div class="footer">
    <p>This document was generated on ${new Date().toLocaleString()} for Class/Owner review.</p>
    <p>Change Request No: ${changeRequest.change_request_no || 'N/A'} | Vessel: ${ship?.vessel_name || 'N/A'}</p>
  </div>
</body>
</html>
  `

  return html
}

/* export change record as html and open in new window for printing */
export function exportChangeRecord(
  changeRequest: ChangeRequestRow,
  ship: Ship | null,
  changePlan: ChangePlanRow | null,
  securityReview: SecurityReviewRow | null,
  systemName?: string | null
): void {
  const html = generateChangeRecordHTML(changeRequest, ship, changePlan, securityReview, systemName)
  
  // create a new window with the html content
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow popups to export the change record')
    return
  }
  
  printWindow.document.write(html)
  printWindow.document.close()
  
  // wait for content to load, then focus (user can print or save as pdf)
  printWindow.onload = () => {
    printWindow.focus()
  }
}

